import { apiClient } from "./client";
import { User } from "@/types";

/**
 * Запрос на вход
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Ответ на вход
 */
export interface LoginResponse {
  token: {
    access_token: string;
    refresh_token: string;
    expires_at: string;
  };
  user: {
    id: string;
    tenant_id: string;
    tenant_name: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    email_verified: boolean;
  };
}

/**
 * API для аутентификации
 */
export const authApi = {
  /**
   * Вход в систему
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post("/auth/login", data);
    return response.data;
  },

  /**
   * Регистрация
   * consent — обязательное согласие с политикой обработки ПДн (ст. 9 152-ФЗ, SUP-86).
   */
  register: async (data: {
    email: string;
    password: string;
    consent: boolean;
  }): Promise<LoginResponse> => {
    const response = await apiClient.post("/auth/register", data);
    return response.data;
  },

  /**
   * Обновление токена
   */
  refresh: async (refreshToken: string) => {
    const response = await apiClient.post("/auth/refresh", {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  /**
   * Выход из системы
   */
  logout: async () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  },

  /**
   * Обновление профиля
   */
  updateProfile: async (data: {
    first_name?: string;
    last_name?: string;
    email?: string;
  }): Promise<User> => {
    const response = await apiClient.put("/auth/me", data);
    return response.data;
  },

  /**
   * Смена пароля
   */
  changePassword: async (data: {
    current_password: string;
    new_password: string;
  }) => {
    await apiClient.put("/auth/me/password", data);
  },

  /**
   * Запрос на сброс пароля
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post("/auth/forgot-password", { email });
    return response.data;
  },

  /**
   * Проверка токена сброса пароля
   */
  verifyResetToken: async (token: string): Promise<{ valid: boolean }> => {
    const response = await apiClient.get(
      `/auth/verify-reset-token?token=${token}`,
    );
    return response.data;
  },

  /**
   * Сброс пароля
   */
  resetPassword: async (
    token: string,
    password: string,
  ): Promise<{ message: string }> => {
    const response = await apiClient.post("/auth/reset-password", {
      token,
      password,
    });
    return response.data;
  },

  /**
   * Подтверждение email по токену из письма
   */
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await apiClient.get(`/auth/verify-email?token=${token}`);
    return response.data;
  },

  /**
   * Повторная отправка письма подтверждения email
   */
  resendVerification: async (): Promise<{ message: string }> => {
    const response = await apiClient.post("/auth/resend-verification");
    return response.data;
  },

  /**
   * Получить список пользователей тенанта
   */
  listUsers: async (): Promise<{ users: User[] }> => {
    const response = await apiClient.get<{ users: User[] }>("/users");
    return {
      ...response.data,
      users: response.data.users ?? [],
    };
  },

  /**
   * Удалить пользователя
   */
  deleteUser: async (userId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/users/${userId}`);
    return response.data;
  },

  /**
   * Удалить тенант и всех пользователей
   */
  deleteTenant: async (tenantId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/tenants/${tenantId}`);
    return response.data;
  },

  /**
   * Экспорт ПДн текущего пользователя (ст. 14 152-ФЗ, SUP-98).
   * Возвращает все хранимые ПДн в машиночитаемом виде.
   */
  exportMyData: async (): Promise<unknown> => {
    const response = await apiClient.get("/auth/me/export", {
      params: { format: "json" },
    });
    return response.data;
  },

  /**
   * Self-service удаление аккаунта (ст. 21 152-ФЗ, SUP-91).
   * Требует подтверждения паролем; confirm_tenant_deletion подтверждает
   * удаление данных компании, если пользователь — последний участник.
   */
  deleteMe: async (
    password: string,
    confirmTenantDeletion = false,
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete("/auth/me", {
      data: { password, confirm_tenant_deletion: confirmTenantDeletion },
    });
    return response.data;
  },
};
